// All per-type structured widgets live here. Each widget is defensive
// about its input value — if the DB has a weirder shape than expected
// (a partial object, null, etc.) it renders sensible defaults rather
// than crashing. Each widget returns a typed value on change so the
// parent can save without JSON.parse gymnastics.

import { useMemo, useState } from 'react';
import Icon from '../../components/Icon';
import type { AdminThemePalette } from '../../games/types';

import type { DifficultySubType, Widget } from './widgetSchema';

// ─── Primitive widgets ───────────────────────────────────────────────

export function ToggleWidget({ value, onChange, theme }: { value: boolean; onChange: (v: boolean) => void; theme: AdminThemePalette }) {
  const t = theme;
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        onClick={() => onChange(!value)}
        className={`relative w-14 h-8 rounded-full transition ${value ? t.accent : 'bg-neutral-100 dark:bg-neutral-700'}`}
      >
        <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition
                          ${value ? 'translate-x-6' : 'translate-x-0'}`} />
      </button>
      <span className={`text-sm font-semibold ${value ? 'text-emerald-400' : 'text-neutral-400'}`}>
        {value ? 'ON' : 'OFF'}
      </span>
    </div>
  );
}

export function EnumWidget({ value, onChange, options, theme }: {
  value: string | null;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string; icon?: string }>;
  theme: AdminThemePalette;
}) {
  const t = theme;
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = String(value) === o.value;
        return (
          <button key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition
              ${active
                ? `${t.accent} text-white border-transparent shadow`
                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-700 hover:border-neutral-500'}`}
          >
            {o.icon && <Icon name={o.icon} size={16} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function SliderWidget({
  value, onChange, min, max, step, suffix, theme,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number; max: number; step: number; suffix?: string;
  theme: AdminThemePalette;
}) {
  const clamped = Number.isFinite(value) ? value : min;
  const rangeAccent = theme.rangeAccent;
  return (
    <div className="flex items-center gap-3">
      <input type="range" min={min} max={max} step={step}
        value={clamped}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`flex-1 h-2 rounded-full ${rangeAccent}`}
      />
      <div className="flex items-center gap-1">
        <input type="number" min={min} max={max} step={step}
          value={clamped}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-20 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 px-2 py-1 text-sm text-right
                     focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
        />
        {suffix && <span className="text-xs text-neutral-400 w-10 pl-1">{suffix}</span>}
      </div>
    </div>
  );
}

export function IntegerWidget({
  value, onChange, min, max, suffix,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number; max?: number; suffix?: string;
}) {
  const v = Number.isFinite(value) ? value : (min ?? 0);
  const lo = min ?? 0;
  const hi = max ?? Math.max(lo + 10, v * 3, 100);
  return (
    <div className="flex items-center gap-3">
      <button type="button"
        onClick={() => onChange(Math.max(lo, Math.floor(v - 1)))}
        className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-100 dark:bg-neutral-700 grid place-items-center"
      >
        <Icon name="remove" size={18} />
      </button>
      <input type="number" min={lo} max={hi} step={1}
        value={v}
        onChange={(e) => onChange(Math.floor(Number(e.target.value) || 0))}
        className="w-24 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 px-2 py-2 text-sm text-center font-semibold
                   focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
      />
      <button type="button"
        onClick={() => onChange(Math.min(hi, Math.floor(v + 1)))}
        className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-100 dark:bg-neutral-700 grid place-items-center"
      >
        <Icon name="add" size={18} />
      </button>
      {suffix && <span className="text-xs text-neutral-400">{suffix}</span>}
    </div>
  );
}

export function NumberWidget({
  value, onChange, step = 1, min, max,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number; min?: number; max?: number;
}) {
  const v = Number.isFinite(value) ? value : (min ?? 0);
  return (
    <div className="flex items-center gap-2">
      <input type="number" step={step} min={min} max={max}
        value={v}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-36 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm
                   focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
      />
    </div>
  );
}

// ─── Difficulty map (easy / medium / hard) ──────────────────────────

export function DifficultyMapWidget({
  value, onChange, sub, theme,
  min: minOverride, max: maxOverride, step: stepOverride,
  suffix: suffixOverride, integer,
}: {
  value: { easy?: number; medium?: number; hard?: number } | null;
  onChange: (v: { easy: number; medium: number; hard: number }) => void;
  sub: DifficultySubType;
  theme: AdminThemePalette;
  // Override range/step/suffix when sub === 'number'. Ignored for
  // 'percent' / 'probability' which have fixed semantics.
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  integer?: boolean;
}) {
  const normalised = {
    easy:   typeof value?.easy   === 'number' ? value.easy   : (sub === 'probability' ? 0.3 : sub === 'percent' ? 30 : 1),
    medium: typeof value?.medium === 'number' ? value.medium : (sub === 'probability' ? 0.6 : sub === 'percent' ? 60 : 1),
    hard:   typeof value?.hard   === 'number' ? value.hard   : (sub === 'probability' ? 0.9 : sub === 'percent' ? 90 : 1),
  };
  const cfg = sub === 'percent'
    ? { min: 0, max: 100, step: 1,    suffix: '%' }
    : sub === 'probability'
      ? { min: 0, max: 1,   step: 0.01, suffix: '' }
      : {
          min:    minOverride    ?? 0,
          max:    maxOverride    ?? 10,
          step:   stepOverride   ?? (integer ? 1 : 0.1),
          suffix: suffixOverride ?? (integer ? '' : '×'),
        };

  function set(k: 'easy' | 'medium' | 'hard', v: number) {
    const coerced = integer && sub === 'number' ? Math.round(v) : v;
    onChange({ ...normalised, [k]: coerced });
  }

  const rows: Array<['easy' | 'medium' | 'hard', string, string]> = [
    ['easy',   'Easy',   'sentiment_satisfied'],
    ['medium', 'Medium', 'sentiment_neutral'],
    ['hard',   'Hard',   'local_fire_department'],
  ];
  return (
    <div className="space-y-2">
      {rows.map(([k, label, icon]) => (
        <div key={k} className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 w-20 shrink-0">
            <Icon name={icon} size={16} className="text-neutral-400" />
            <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">{label}</span>
          </div>
          <div className="flex-1">
            <SliderWidget
              value={normalised[k]}
              onChange={(v) => set(k, v)}
              min={cfg.min} max={cfg.max} step={cfg.step} suffix={cfg.suffix}
              theme={theme}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Key-value map with fixed keys ───────────────────────────────────

export function KeyValueMapWidget({
  value, onChange, keys, valueType, theme,
}: {
  value: Record<string, unknown> | null;
  onChange: (v: Record<string, string | number>) => void;
  keys: Array<{ key: string; label: string; icon?: string }>;
  valueType: 'number' | 'string' | 'probability' | 'percent';
  theme: AdminThemePalette;
}) {
  const current: Record<string, string | number> = {};
  for (const k of keys) {
    const raw = value?.[k.key];
    if (valueType === 'string') current[k.key] = typeof raw === 'string' ? raw : '';
    else current[k.key] = typeof raw === 'number' ? raw : 0;
  }
  function set(k: string, v: string | number) {
    onChange({ ...current, [k]: v });
  }
  return (
    <div className="space-y-2">
      {keys.map((k) => (
        <div key={k.key} className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 w-44 shrink-0">
            {k.icon && <Icon name={k.icon} size={16} className="text-neutral-400" />}
            <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">{k.label}</span>
          </div>
          <div className="flex-1">
            {valueType === 'string' && (
              <input type="text" value={current[k.key] as string}
                onChange={(e) => set(k.key, e.target.value)}
                className="w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm font-mono
                           focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
            )}
            {valueType === 'number' && (
              <NumberWidget value={current[k.key] as number} onChange={(v) => set(k.key, v)} step={1} />
            )}
            {valueType === 'percent' && (
              <SliderWidget value={current[k.key] as number} onChange={(v) => set(k.key, v)}
                min={0} max={100} step={1} suffix="%" theme={theme} />
            )}
            {valueType === 'probability' && (
              <SliderWidget value={current[k.key] as number} onChange={(v) => set(k.key, v)}
                min={0} max={1} step={0.01} theme={theme} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Bet-threshold → mode list ───────────────────────────────────────
// Value shape: Array<{minBet: number, mode: 'easy'|'medium'|'hard'}>

type BetModeRow = { minBet: number; mode: string };

export function BetModeListWidget({
  value, onChange, theme,
}: {
  value: unknown;
  onChange: (v: BetModeRow[]) => void;
  theme: AdminThemePalette;
}) {
  const t = theme;
  const rows: BetModeRow[] = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return (value as unknown[])
      .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
      .map((r) => ({
        minBet: typeof r.minBet === 'number' ? r.minBet : 0,
        mode: typeof r.mode === 'string' ? r.mode : 'easy',
      }))
      .sort((a, b) => b.minBet - a.minBet);
  }, [value]);

  function save(next: BetModeRow[]) {
    onChange([...next].sort((a, b) => b.minBet - a.minBet));
  }
  function update(i: number, patch: Partial<BetModeRow>) {
    const next = rows.map((r, j) => j === i ? { ...r, ...patch } : r);
    save(next);
  }
  function remove(i: number) { save(rows.filter((_, j) => j !== i)); }
  function add() {
    const lastBet = rows.length ? rows[rows.length - 1].minBet : 0;
    save([...rows, { minBet: lastBet + 100, mode: 'easy' }]);
  }

  return (
    <div>
      <p className="text-[11px] text-neutral-500 mb-2">
        Rules are checked top-to-bottom (highest stake first). Each row means: if the match bet is at least
        this much, use this difficulty.
      </p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-300 dark:border-neutral-700 p-2">
            <span className="text-xs text-neutral-500">Bet ≥</span>
            <input type="number" min={0} step={1}
              value={r.minBet}
              onChange={(e) => update(i, { minBet: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
              className="w-28 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-2 py-1.5 text-sm text-right
                         focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
            />
            <span className="text-xs text-neutral-500">→</span>
            <select value={r.mode}
              onChange={(e) => update(i, { mode: e.target.value })}
              className="rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-2 py-1.5 text-sm
                         focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
            >
              <option value="easy">Easy bot</option>
              <option value="medium">Medium bot</option>
              <option value="hard">Hard bot</option>
            </select>
            <button type="button"
              onClick={() => remove(i)}
              className="ml-auto w-8 h-8 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-700 dark:text-red-300 grid place-items-center"
              aria-label="Remove row"
            >
              <Icon name="delete" size={16} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold
                    ${t.accent} ${t.accentHover} text-white`}
      >
        <Icon name="add" size={16} /> Add rule
      </button>
    </div>
  );
}

// ─── Personality-weight rules (per bet bracket) ─────────────────────
// Each rule has a minBet and three weights (easy/medium/hard). At
// match time, the first rule whose minBet is ≤ the match's bet
// decides the WEIGHTS used to roll a personality. Weights are
// relative chances — they don't have to sum to 100. All three
// personality types remain possible at every bet, just at the rates
// the admin set.

type WeightRule = { minBet: number; weights: { easy: number; medium: number; hard: number } };

export function PersonalityWeightRulesWidget({
  value, onChange, theme,
}: {
  value: unknown;
  onChange: (v: WeightRule[]) => void;
  theme: AdminThemePalette;
}) {
  const t = theme;
  const rows: WeightRule[] = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return (value as unknown[])
      .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
      .map((r) => {
        const w = (r.weights && typeof r.weights === 'object') ? r.weights as Record<string, unknown> : {};
        return {
          minBet: Math.max(0, Math.floor(Number(r.minBet) || 0)),
          weights: {
            easy:   Math.max(0, Math.floor(Number(w.easy)   || 0)),
            medium: Math.max(0, Math.floor(Number(w.medium) || 0)),
            hard:   Math.max(0, Math.floor(Number(w.hard)   || 0)),
          },
        };
      })
      .sort((a, b) => b.minBet - a.minBet);
  }, [value]);

  function save(next: WeightRule[]) {
    onChange([...next].sort((a, b) => b.minBet - a.minBet));
  }
  function update(i: number, patch: Partial<WeightRule> | { [K in keyof WeightRule['weights']]?: number }) {
    const next = rows.map((r, j) => {
      if (j !== i) return r;
      if ('weights' in patch || 'minBet' in patch) {
        return { ...r, ...patch } as WeightRule;
      }
      return { ...r, weights: { ...r.weights, ...patch } } as WeightRule;
    });
    save(next);
  }
  function remove(i: number) { save(rows.filter((_, j) => j !== i)); }
  function add() {
    const lastBet = rows.length ? rows[0].minBet : 0;
    save([...rows, { minBet: lastBet + 50, weights: { easy: 33, medium: 34, hard: 33 } }]);
  }

  // Live preview: pick a bet, see which row matches and what the
  // three percentages would resolve to.
  const [previewBet, setPreviewBet] = useState<number>(0);
  const matched = rows.find((r) => previewBet >= r.minBet);
  const total = matched ? (matched.weights.easy + matched.weights.medium + matched.weights.hard) : 0;
  const pct = (n: number) => total > 0 ? Math.round((n / total) * 100) : 0;

  return (
    <div>
      <p className="text-[11px] text-neutral-500 mb-2">
        Rules are checked top-down (highest bet first). The first row whose "bet is at least X" condition
        holds wins. Weights are relative chances — all three personality types can still appear at any
        bet, just at the frequency you set. Example: easy=5, medium=15, hard=80 → 5% easy bots, 15% medium, 80% hard for that bracket.
      </p>
      <div className="space-y-2">
        {rows.map((r, i) => {
          const rowTotal = r.weights.easy + r.weights.medium + r.weights.hard;
          return (
            <div key={i} className="rounded-lg bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-300 dark:border-neutral-700 p-2 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Bet ≥</span>
                <input type="number" min={0} step={1}
                  value={r.minBet}
                  onChange={(e) => update(i, { minBet: Math.max(0, Math.floor(Number(e.target.value) || 0)) } as Partial<WeightRule>)}
                  className="w-24 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-2 py-1.5 text-sm text-right
                             focus:outline-none focus:ring-2 focus:ring-fuchsia-500" />
                <span className="text-[10px] text-neutral-500 ml-auto">
                  Total: <b className={rowTotal > 0 ? 'text-neutral-700 dark:text-neutral-300' : 'text-red-400'}>{rowTotal}</b>
                </span>
                <button type="button" onClick={() => remove(i)}
                  className="w-7 h-7 rounded-md bg-red-600/20 hover:bg-red-600/40 text-red-700 dark:text-red-300 grid place-items-center"
                  aria-label="Remove rule">
                  <Icon name="delete" size={14} />
                </button>
              </div>
              <WeightRow label="Easy"   color="emerald" icon="sentiment_satisfied"
                value={r.weights.easy}   total={rowTotal}
                onChange={(v) => update(i, { easy: v })} theme={theme} />
              <WeightRow label="Medium" color="amber"   icon="sentiment_neutral"
                value={r.weights.medium} total={rowTotal}
                onChange={(v) => update(i, { medium: v })} theme={theme} />
              <WeightRow label="Hard"   color="red"     icon="local_fire_department"
                value={r.weights.hard}   total={rowTotal}
                onChange={(v) => update(i, { hard: v })} theme={theme} />
            </div>
          );
        })}
      </div>
      <button type="button" onClick={add}
        className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold
                    ${t.accent} ${t.accentHover} text-white`}>
        <Icon name="add" size={16} /> Add rule
      </button>

      <div className="mt-4 rounded-lg bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-800 p-3">
        <div className="flex items-center gap-2 text-xs text-neutral-400 mb-2">
          <Icon name="calculate" size={14} /> Try a bet amount to see which rule will apply:
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="number" min={0} step={1}
            value={previewBet} onChange={(e) => setPreviewBet(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            placeholder="Bet"
            className="w-28 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm font-mono
                       focus:outline-none focus:ring-2 focus:ring-fuchsia-500" />
          {matched && total > 0 ? (
            <>
              <span className="text-[11px] text-neutral-500">→ matches <b>Bet ≥ {matched.minBet}</b> →</span>
              <PreviewPill color="emerald" label="Easy"   pct={pct(matched.weights.easy)} />
              <PreviewPill color="amber"   label="Medium" pct={pct(matched.weights.medium)} />
              <PreviewPill color="red"     label="Hard"   pct={pct(matched.weights.hard)} />
            </>
          ) : (
            <span className="text-[11px] text-neutral-500">
              {rows.length === 0 ? 'No rules — add at least one.' : 'No rule matches this bet (all rules need total &gt; 0).'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function WeightRow({ label, color, icon, value, total, onChange, theme }: {
  label: string;
  color: 'emerald' | 'amber' | 'red';
  icon: string;
  value: number;
  total: number;
  onChange: (v: number) => void;
  theme: AdminThemePalette;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const colorCls = color === 'emerald' ? 'text-emerald-400'
    : color === 'amber' ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 w-24 shrink-0">
        <Icon name={icon} size={14} className={colorCls} />
        <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">{label}</span>
      </div>
      <input type="range" min={0} max={100} step={1} value={value}
        onChange={(e) => onChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
        className={`flex-1 h-2 rounded-full ${
          theme.rangeAccent}`} />
      <input type="number" min={0} max={200} step={1} value={value}
        onChange={(e) => onChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
        className="w-14 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-1 py-1 text-xs text-right font-mono
                   focus:outline-none focus:ring-2 focus:ring-fuchsia-500" />
      <span className="w-14 text-right text-[11px] text-neutral-500 font-mono">
        {pct}%
      </span>
    </div>
  );
}

function PreviewPill({ color, label, pct }: { color: 'emerald' | 'amber' | 'red'; label: string; pct: number }) {
  const cls = color === 'emerald'
    ? 'bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border-emerald-600/30'
    : color === 'amber'
      ? 'bg-amber-600/20 text-amber-700 dark:text-amber-300 border-amber-600/30'
      : 'bg-red-600/20 text-red-700 dark:text-red-300 border-red-600/30';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cls}`}>
      {label} <b>{pct}%</b>
    </span>
  );
}

// ─── Personality list ────────────────────────────────────────────────
// Value shape: { [label]: [minMul, maxMul, weight] }

type PersonalityRow = { label: string; minMul: number; maxMul: number; weight: number };

export function PersonalityListWidget({
  value, onChange, theme,
}: {
  value: unknown;
  onChange: (v: Record<string, [number, number, number]>) => void;
  theme: AdminThemePalette;
}) {
  const t = theme;
  const rows: PersonalityRow[] = useMemo(() => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const out: PersonalityRow[] = [];
    for (const [label, spec] of Object.entries(value as Record<string, unknown>)) {
      if (!Array.isArray(spec) || spec.length < 3) continue;
      out.push({
        label,
        minMul: typeof spec[0] === 'number' ? spec[0] : 0.8,
        maxMul: typeof spec[1] === 'number' ? spec[1] : 1.2,
        weight: typeof spec[2] === 'number' ? spec[2] : 50,
      });
    }
    return out;
  }, [value]);

  function save(next: PersonalityRow[]) {
    const obj: Record<string, [number, number, number]> = {};
    for (const r of next) {
      if (!r.label.trim()) continue;
      obj[r.label] = [r.minMul, r.maxMul, r.weight];
    }
    onChange(obj);
  }
  function update(i: number, patch: Partial<PersonalityRow>) {
    const next = rows.map((r, j) => j === i ? { ...r, ...patch } : r);
    save(next);
  }
  function remove(i: number) { save(rows.filter((_, j) => j !== i)); }
  function add() { save([...rows, { label: 'new_personality', minMul: 0.9, maxMul: 1.2, weight: 20 }]); }

  return (
    <div>
      <p className="text-[11px] text-neutral-500 mb-2">
        Each match picks ONE personality (weighted). Min/Max speed multipliers scale the bot's delays:
        0.5 = twice as fast, 2.0 = twice as slow. Weight controls how often that personality shows up.
      </p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr,auto,auto,auto,auto] gap-2 items-center
                                  rounded-lg bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-300 dark:border-neutral-700 p-2">
            <input type="text"
              value={r.label}
              onChange={(e) => update(i, { label: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
              placeholder="label"
              className="rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-2 py-1.5 text-sm
                         focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
            />
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-neutral-500">MIN</span>
              <input type="number" step={0.05} min={0.1} max={5}
                value={r.minMul}
                onChange={(e) => update(i, { minMul: Number(e.target.value) || 0 })}
                className="w-16 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-1 py-1.5 text-sm text-right
                           focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-neutral-500">MAX</span>
              <input type="number" step={0.05} min={0.1} max={5}
                value={r.maxMul}
                onChange={(e) => update(i, { maxMul: Number(e.target.value) || 0 })}
                className="w-16 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-1 py-1.5 text-sm text-right
                           focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-neutral-500">WEIGHT</span>
              <input type="number" step={1} min={0} max={1000}
                value={r.weight}
                onChange={(e) => update(i, { weight: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                className="w-16 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 px-1 py-1.5 text-sm text-right
                           focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
            </div>
            <button type="button" onClick={() => remove(i)}
              className="w-8 h-8 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-700 dark:text-red-300 grid place-items-center">
              <Icon name="delete" size={16} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold
                    ${t.accent} ${t.accentHover} text-white`}
      >
        <Icon name="add" size={16} /> Add personality
      </button>
    </div>
  );
}

// ─── Number list ─────────────────────────────────────────────────────

export function NumberListWidget({
  value, onChange, min, max, allowZero, theme,
}: {
  value: unknown;
  onChange: (v: number[]) => void;
  min?: number; max?: number; allowZero?: boolean;
  theme: AdminThemePalette;
}) {
  const t = theme;
  const rows: number[] = Array.isArray(value) ? (value as unknown[]).map((v) => Number(v) || 0) : [];
  const lo = min ?? 0;
  const hi = max ?? Number.MAX_SAFE_INTEGER;

  function save(next: number[]) {
    // Sort ascending, allow 0 if allowZero is set (for BET_TIERS to include free matches).
    const filtered = next.filter((v) => Number.isFinite(v) && v >= lo && (allowZero || v > lo));
    onChange(Array.from(new Set(filtered)).sort((a, b) => a - b));
  }
  function update(i: number, v: number) {
    const next = rows.map((r, j) => j === i ? v : r);
    save(next);
  }
  function remove(i: number) { save(rows.filter((_, j) => j !== i)); }
  function add() {
    const last = rows.length ? rows[rows.length - 1] : lo;
    save([...rows, last + (last > 0 ? last : 10)]);
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {rows.map((n, i) => (
          <div key={i} className="inline-flex items-center gap-1 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 pl-3 pr-1 py-1.5">
            <input type="number" min={lo} max={hi} step={1}
              value={n}
              onChange={(e) => update(i, Math.floor(Number(e.target.value) || 0))}
              className="w-20 bg-transparent text-sm text-right focus:outline-none"
            />
            <button type="button" onClick={() => remove(i)}
              className="w-6 h-6 rounded-md bg-red-600/20 hover:bg-red-600/40 text-red-700 dark:text-red-300 grid place-items-center">
              <Icon name="close" size={14} />
            </button>
          </div>
        ))}
        <button type="button" onClick={add}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold
                      ${t.accent} ${t.accentHover} text-white`}
        >
          <Icon name="add" size={14} /> Add
        </button>
      </div>
    </div>
  );
}

// ─── String list ─────────────────────────────────────────────────────

export function StringListWidget({
  value, onChange, placeholder, theme,
}: {
  value: unknown;
  onChange: (v: string[]) => void;
  placeholder?: string;
  theme: AdminThemePalette;
}) {
  const t = theme;
  const rows: string[] = Array.isArray(value) ? (value as unknown[]).map((v) => String(v ?? '')) : [];

  function save(next: string[]) {
    const cleaned = next.map((s) => s.trim()).filter(Boolean);
    onChange(Array.from(new Set(cleaned)));
  }
  function update(i: number, v: string) {
    const next = rows.map((r, j) => j === i ? v : r);
    // Don't clean while typing — only on add / remove / save.
    onChange(next);
  }
  function remove(i: number) { save(rows.filter((_, j) => j !== i)); }
  function add() { onChange([...rows, '']); }

  return (
    <div>
      <div className="max-h-72 overflow-y-auto pr-1 space-y-1 bg-neutral-50 dark:bg-neutral-900/40 border border-neutral-200 dark:border-neutral-800 rounded-lg p-2">
        {rows.length === 0 && (
          <p className="text-xs text-neutral-500 px-2 py-4 text-center">No entries yet. Click "Add" to start.</p>
        )}
        {rows.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] text-neutral-500 w-6 text-right">{i + 1}</span>
            <input type="text" value={s}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 px-2 py-1.5 text-sm
                         focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
            />
            <button type="button" onClick={() => remove(i)}
              className="w-8 h-8 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-700 dark:text-red-300 grid place-items-center">
              <Icon name="delete" size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button type="button" onClick={add}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold
                      ${t.accent} ${t.accentHover} text-white`}
        >
          <Icon name="add" size={14} /> Add entry
        </button>
        <span className="text-xs text-neutral-500">{rows.filter(Boolean).length} total</span>
      </div>
    </div>
  );
}

// ─── Plain string (single-line or multiline) ─────────────────────────
// Used by config keys that are simple admin-editable messages — e.g.
// Plinko's maintenance_message. Stores as a plain JSON string (no
// array wrapper). `multiline` switches between <input> and <textarea>.
export function StringWidget({
  value, onChange, placeholder, multiline, maxLength, theme,
}: {
  value: unknown;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
  theme: AdminThemePalette;
}) {
  const t = theme;
  const v = value == null ? '' : String(value);
  const baseClass = `w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm
                     focus:outline-none focus:ring-2 focus:ring-fuchsia-500`;
  return (
    <div>
      {multiline ? (
        <textarea
          value={v}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={3}
          className={baseClass + ' resize-y min-h-[64px]'}
        />
      ) : (
        <input
          type="text"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          className={baseClass}
        />
      )}
      {maxLength && (
        <div className="text-[10px] text-neutral-500 mt-1 text-right">
          {v.length}/{maxLength}
        </div>
      )}
    </div>
  );
}

// ─── Checkboxes (finite set of allowed values) ───────────────────────

export function CheckboxesWidget({
  value, onChange, options, theme,
}: {
  value: unknown;
  onChange: (v: Array<number | string>) => void;
  options: Array<{ value: number | string; label: string }>;
  theme: AdminThemePalette;
}) {
  const t = theme;
  const selected = new Set<number | string>(
    Array.isArray(value) ? (value as Array<number | string>) : []
  );
  function toggle(v: number | string) {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = selected.has(o.value);
        return (
          <button key={String(o.value)}
            type="button"
            onClick={() => toggle(o.value)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition
              ${on
                ? `${t.accent} text-white border-transparent shadow`
                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-700 hover:border-neutral-500'}`}
          >
            <Icon name={on ? 'check_box' : 'check_box_outline_blank'} size={18} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Read-only ───────────────────────────────────────────────────────

export function ReadonlyWidget({ value, reason }: { value: unknown; reason?: string }) {
  const display = (() => {
    try { return JSON.stringify(value, null, 2); } catch { return String(value); }
  })();
  return (
    <div>
      {reason && (
        <div className="mb-2 inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold
                        bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300">
          <Icon name="lock" size={12} /> Read-only — {reason}
        </div>
      )}
      <pre className="max-h-40 overflow-auto rounded-lg bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-800
                      p-3 text-[11px] font-mono text-neutral-400 whitespace-pre-wrap">
        {display}
      </pre>
    </div>
  );
}

// ─── Central dispatcher ─────────────────────────────────────────────

export function WidgetRenderer({
  widget, value, onChange, theme,
}: {
  widget: Widget;
  value: unknown;
  onChange: (v: unknown) => void;
  theme: AdminThemePalette;
}) {
  switch (widget.kind) {
    case 'bool':
      return <ToggleWidget value={!!value} onChange={onChange} theme={theme} />;

    case 'enum':
      return <EnumWidget value={typeof value === 'string' ? value : null}
        onChange={onChange} options={widget.options} theme={theme} />;

    case 'percent':
      return <SliderWidget value={Number(value) || 0} onChange={onChange}
        min={0} max={100} step={1} suffix="%" theme={theme} />;

    case 'probability':
      return <SliderWidget value={Number(value) || 0} onChange={onChange}
        min={0} max={1} step={0.01} theme={theme} />;

    case 'integer':
      return <IntegerWidget value={Number(value) || 0} onChange={onChange}
        min={widget.min} max={widget.max} suffix={widget.suffix} />;

    case 'number':
      return <NumberWidget value={Number(value) || 0} onChange={onChange}
        step={widget.step} min={widget.min} max={widget.max} />;

    case 'duration-ms':
      return <IntegerWidget value={Number(value) || 0} onChange={onChange}
        min={0} max={60_000} suffix="ms" />;

    case 'duration-sec':
      return <IntegerWidget value={Number(value) || 0} onChange={onChange}
        min={0} max={600} suffix="sec" />;

    case 'difficulty-map':
      return <DifficultyMapWidget
        value={(value && typeof value === 'object' && !Array.isArray(value) ? value : null) as never}
        onChange={onChange} sub={widget.sub} theme={theme}
        min={widget.min} max={widget.max} step={widget.step}
        suffix={widget.suffix} integer={widget.integer} />;

    case 'key-value-map':
      return <KeyValueMapWidget
        value={(value && typeof value === 'object' && !Array.isArray(value) ? value : null) as never}
        onChange={onChange} keys={widget.keys} valueType={widget.valueType} theme={theme} />;

    case 'bet-mode-list':
      return <BetModeListWidget value={value} onChange={onChange} theme={theme} />;

    case 'personality-weight-rules':
      return <PersonalityWeightRulesWidget value={value} onChange={onChange} theme={theme} />;

    case 'personality-list':
      return <PersonalityListWidget value={value} onChange={onChange} theme={theme} />;

    case 'number-list':
      return <NumberListWidget value={value} onChange={onChange}
        min={widget.min} max={widget.max} allowZero={widget.allowZero} theme={theme} />;

    case 'string-list':
      return <StringListWidget value={value} onChange={onChange}
        placeholder={widget.placeholder} theme={theme} />;

    case 'string':
      return <StringWidget value={value} onChange={onChange}
        placeholder={widget.placeholder} multiline={widget.multiline}
        maxLength={widget.maxLength} theme={theme} />;

    case 'checkboxes':
      return <CheckboxesWidget value={value} onChange={onChange}
        options={widget.options} theme={theme} />;

    case 'readonly':
    default:
      return <ReadonlyWidget value={value} reason={widget.kind === 'readonly' ? widget.reason : undefined} />;
  }
}
